import GenericErrorMessage from '@/components/GenericErrorMessage';
import { useModals } from '@/components/modals/ModalProvider';
import {
	getProspectCreditCheck,
	getSlikRobotikDetail,
	getSlikRobotikResult,
} from '@/libs/fleet-api/services/prospect';
import {
	FileSearchOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
	Alert,
	Button,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Skeleton,
	Table,
	Tabs,
} from 'antd';
import { FC, Fragment, useState } from 'react';
import AnalisaSLIKFormRobotik from './NAPPage.Fullfillment.slik.robotik';

export type NAPAnalisaSLIKPageProps = { prospectId: string };

const NAPAnalisaSLIKPage: FC<NAPAnalisaSLIKPageProps> = ({
	prospectId = '943P2024110005',
}) => {
	const { openPreviewModal, openManagementModal, openShareholderModal } =
		useModals();
	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ['getProspectCreditCheck', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getProspectCreditCheck(id);
		},
	});

	const {
		mainInformation,
		shareholderSlikInformation,
		managementSlikInformation,
		customerReferralDocuments,
		approvalHistory,
		slikFile,
		slikCheckingStatus,
		slikInquiryMetric,
	} = data?.data || {};

	const isSlikRobotik = slikInquiryMetric?.isRobotic ?? false;
	const isSlikRobotikFailed = slikInquiryMetric?.isRoboticFailed ?? false;
	const isSlikRobotikCancelled =
		slikInquiryMetric?.roboticStatus?.cancelled ?? 0 > 0;
	const isSlikRobotikNotFound =
		slikInquiryMetric?.roboticStatus?.notFound ?? 0 > 0;

	// console.log(slikInquiryMetric);
	// const isSlikRobotik = true;
	// const isSlikRobotikFailed && !isSlikRobotikCancelled && slikInquiryMetric?.roboticStatus?.notFound !==  = t ue;

	// get result slik robotik
	const { data: slikRobotikResult, isLoading: isLoadingSlikRobotikResult } =
		useQuery({
			queryKey: ['getSlikRobotikResult', prospectId] as const,
			queryFn: ({ queryKey }) => {
				const [, id] = queryKey;
				return getSlikRobotikResult(id);
			},
			enabled: !!prospectId,
		});

	// get result detail robotik
	const { data: slikRobotikDetail, isLoading: isLoadingSlikRobotikDetail } =
		useQuery({
			queryKey: ['getSlikRobotikDetail', prospectId] as const,
			queryFn: ({ queryKey }) => {
				const [, id] = queryKey;
				return getSlikRobotikDetail(id);
			},
			enabled: isSlikRobotik && !isSlikRobotikFailed && !isSlikRobotikCancelled,
		});

	const [slikCheckActiveTab, setSlikCheckActiveTab] = useState<
		'manager' | 'shareholder'
	>('manager');

	if (isError) return <GenericErrorMessage />;

	if (isLoading) return null;

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<Fragment>
				{slikCheckingStatus?.id === 1 ? (
					<Fragment>
						<Row gutter={[40, 20]}>
							<Col span={12}>
								<Form.Item label="View Dokumen CES">
									<Button
										type="link"
										onClick={() => {
											const cesDocument = customerReferralDocuments?.find(
												(doc) =>
													doc.documentType.includes(
														'Customer Executive Summary',
													),
											);

											if (!cesDocument) return;
											openPreviewModal(cesDocument.file);
										}}
									>
										Dokumen-CES.pdf
									</Button>
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item label="SLIK Result">
									<Select
										disabled
										value={mainInformation?.slikResult}
									/>
								</Form.Item>
							</Col>
						</Row>
						<Divider />
						<Tabs
							activeKey={slikCheckActiveTab}
							onChange={(key) => {
								setSlikCheckActiveTab(key as typeof slikCheckActiveTab);
							}}
							items={[
								{
									key: 'manager',
									label: 'Pengurus',
									children: (
										<Table
											bordered
											style={{ marginBottom: 20 }}
											pagination={false}
											dataSource={
												managementSlikInformation?.map((manager, key) => {
													return { key, ...manager };
												}) ?? []
											}
											columns={[
												{
													title: 'View Detail',
													width: 110,
													align: 'center',
													render: () => {
														return (
															<a
																href="#"
																style={{ color: '#bbb' }}
																onClick={(e) => {
																	e.preventDefault();
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
												{
													title: 'View Ideb',
													width: 110,
													align: 'center',
													render: () => {
														return (
															<a
																href="#"
																style={{ color: '#bbb' }}
																onClick={(e) => {
																	e.preventDefault();
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
												{
													title: 'Upload Ideb',
													width: 110,
													align: 'center',
													render: () => {
														return (
															<a
																href="#"
																style={{ color: '#bbb' }}
																onClick={(e) => {
																	e.preventDefault();
																}}
															>
																<UploadOutlined />
															</a>
														);
													},
												},
												{
													title: 'Nama Pemegang Saham',
													width: 300,
													render: (_, record) => record.name,
												},
												{
													title: 'SLIK Result',
													render: (_, record) => {
														return (
															<Fragment>
																<Select
																	size="small"
																	style={{ width: 80 }}
																	disabled
																	value={record.slikResult}
																/>
															</Fragment>
														);
													},
												},
											]}
										/>
									),
								},
								{
									key: 'shareholder',
									label: 'Pemegang Saham',
									children: (
										<Table
											bordered
											style={{ marginBottom: 20 }}
											pagination={false}
											dataSource={
												shareholderSlikInformation?.map((shareholder, key) => {
													return { key, ...shareholder };
												}) ?? []
											}
											columns={[
												{
													title: 'View Detail',
													width: 110,
													align: 'center',
													render: () => {
														return (
															<a
																href="#"
																style={{ color: '#bbb' }}
																onClick={(e) => {
																	e.preventDefault();
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
												{
													title: 'View Ideb',
													width: 110,
													align: 'center',
													render: () => {
														return (
															<a
																href="#"
																style={{ color: '#bbb' }}
																onClick={(e) => {
																	e.preventDefault();
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
												{
													title: 'Upload Ideb',
													width: 110,
													align: 'center',
													render: () => {
														return (
															<a
																href="#"
																style={{ color: '#bbb' }}
																onClick={(e) => {
																	e.preventDefault();
																}}
															>
																<UploadOutlined />
															</a>
														);
													},
												},
												{
													title: 'Nama Pemegang Saham',
													width: 300,
													render: (_, record) => record.name,
												},
												{
													title: 'SLIK Result',
													render: (_, record) => {
														return (
															<Fragment>
																<Select
																	size="small"
																	style={{ width: 80 }}
																	disabled
																	value={record.slikResult}
																/>
															</Fragment>
														);
													},
												},
											]}
										/>
									),
								},
							]}
						/>
					</Fragment>
				) : (
					<Fragment>
						{isSlikRobotik &&
						!isSlikRobotikFailed &&
						!isSlikRobotikCancelled &&
						slikInquiryMetric?.roboticStatus?.notFound !==
							slikInquiryMetric?.roboticStatus?.total ? (
							<Fragment>
								<Alert
									message={`${
										isSlikRobotikCancelled
											? 'SLIK Robotik di batalkan'
											: isSlikRobotikNotFound
												? 'Sebagian / Semua SLIK Robotik tidak ditemukan'
												: 'Prospect ini menggunakan SLIK Robotik'
									}`}
									type="warning"
									showIcon
									className="custom-alert-info"
								/>
								{isLoadingSlikRobotikDetail || isLoadingSlikRobotikResult ? (
									<Skeleton active />
								) : (
									<AnalisaSLIKFormRobotik
										shareholderSlikInformation={
											shareholderSlikInformation ?? []
										}
										managementSlikInformation={managementSlikInformation ?? []}
										data={slikRobotikDetail?.data ?? []}
										result={slikRobotikResult?.data.details}
									/>
								)}
							</Fragment>
						) : (
							<Fragment>
								<Alert
									message="Prospect ini menggunakan SLIK Manual"
									type="warning"
									showIcon
									className="custom-alert-info"
								/>
								{/* <Typography.Title level={3}>Credit Summary</Typography.Title>
								<Table
									style={{
										marginBottom: 20,
									}}
									scroll={{ x: 'max-content' }}
									loading={isLoading}
									pagination={false}
									dataSource={[{ key: 1 }]}
									columns={[
										{
											title: 'View Detail',
											width: 120,
											align: 'center',
											render: () => (
												<a
													style={{ display: 'block' }}
													onClick={(e) => {
														e.preventDefault();
														openCustomerDetail(mainInformation!.customerId);
													}}
												>
													<FileSearchOutlined />
												</a>
											),
										},
										{
											title: 'View Ideb',
											width: 120,
											align: 'center',
											render: () => (
												<a
													style={{ display: 'block' }}
													onClick={(e) => {
														e.preventDefault();
														if (slikFile) {
															openPreviewModal({
																name: slikFile?.name,
																url: slikFile?.url,
																id: 1,
															});
														}
													}}
												>
													<FileSearchOutlined />
												</a>
											),
										},
										{
											title: 'Nama Debitur',
											render: () => debtorSlikInformation?.name ?? '-',
										},
										{
											title: 'SLIK Result',
											render: () =>
												mainInformation?.slikResult
													? 'Kol ' + mainInformation.slikResult
													: '-',
										},
										{
											title: 'NPWP',
											render: () => debtorSlikInformation?.npwp ?? '-',
										},
										{
											title: 'Plafon',
											render: () => '-',
										},
										{
											title: 'Baki Debet',
											render: () => '-',
										},
										{
											title: 'Tunggakan Pokok',
											render: () => '-',
										},
									]}
								/> */}
								<Row gutter={[40, 20]}>
									<Col span={12}>
										<Form.Item label="View SLIK">
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													if (slikFile) {
														openPreviewModal({
															name: slikFile?.name,
															url: slikFile?.url,
															id: 1,
														});
													}
												}}
											>
												{slikFile?.name}
											</a>
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item label="SLIK Result">
											<Input
												value={mainInformation?.slikResult}
												disabled
											/>
										</Form.Item>
									</Col>
								</Row>
								<Divider />
								<Tabs
									style={{ marginBottom: 20 }}
									items={[
										{
											label: 'Pengurus',
											key: 'Pengurus',
											children: (
												<Table
													scroll={{ x: 'max-content' }}
													loading={isLoading}
													pagination={false}
													dataSource={
														managementSlikInformation?.map((manager, key) => {
															return { key, ...manager };
														}) ?? []
													}
													columns={[
														{
															title: 'View Detail',
															width: 120,
															align: 'center',
															render: (_, record) => (
																<a
																	style={{ display: 'block' }}
																	onClick={(e) => {
																		e.preventDefault();
																		openManagementModal(record!.id);
																	}}
																>
																	<FileSearchOutlined />
																</a>
															),
														},
														{
															title: 'View Ideb',
															width: 120,
															align: 'center',
															render: (_, record) => {
																return (
																	<a
																		style={
																			!record.idebFile?.id
																				? { color: '#bababa' }
																				: {}
																		}
																		onClick={(e) => {
																			e.preventDefault();
																			if (record.idebFile) {
																				openPreviewModal(record.idebFile);
																			}
																		}}
																	>
																		<FileSearchOutlined />
																	</a>
																);
															},
														},
														{
															title: 'Nama Pengurus',
															render: (_, record) => record.name,
														},
														{
															title: 'SLIK Result',
															render: (_, record) => record.slikResult,
														},
													]}
												/>
											),
										},
										{
											label: 'Pemegang Saham',
											key: 'Pemegang Saham',
											children: (
												<Table
													scroll={{ x: 'max-content' }}
													loading={isLoading}
													pagination={false}
													dataSource={
														shareholderSlikInformation?.map(
															(shareholder, key) => {
																return { key, ...shareholder };
															},
														) ?? []
													}
													columns={[
														{
															title: 'View Detail',
															width: 120,
															align: 'center',
															render: (_, record) => (
																<a
																	style={{ display: 'block' }}
																	onClick={(e) => {
																		e.preventDefault();
																		openShareholderModal(record.id);
																	}}
																>
																	<FileSearchOutlined />
																</a>
															),
														},
														{
															title: 'View Ideb',
															width: 120,
															align: 'center',
															render: (_, record) => (
																<a
																	style={
																		!record.idebFile?.id
																			? { color: '#bababa' }
																			: {}
																	}
																	onClick={(e) => {
																		e.preventDefault();
																		if (record.idebFile) {
																			openPreviewModal(record.idebFile);
																		}
																	}}
																>
																	<FileSearchOutlined />
																</a>
															),
														},
														{
															title: 'Nama Pemegang Saham',
															render: (_, record) => record.name,
														},
														{
															title: 'SLIK Result',
															render: (_, record) => record.slikResult,
														},
													]}
												/>
											),
										},
									]}
								/>
								{/* <Button
										type="primary"
										style={{
											width: 200,
											display: 'block',
											marginLeft: 'auto',
											marginTop: 20,
											marginBottom: 20,
										}}
									>
										VIEW ALL DETAIL
									</Button> */}
								<Divider />
								<Row gutter={[40, 20]}>
									<Col span={24}>
										<Form.Item
											label="Catatan"
											labelCol={{ span: 6, style: { alignSelf: 'center' } }}
											wrapperCol={{ style: { marginLeft: -10 } }}
										>
											<p>
												{
													approvalHistory?.[approvalHistory?.length - 1]
														?.approvalNote
												}
											</p>
										</Form.Item>
									</Col>
								</Row>
							</Fragment>
						)}
					</Fragment>
				)}
			</Fragment>
		</Fragment>
	);
};

export default NAPAnalisaSLIKPage;
